import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import {
  DivineServiceTypeManagementService,
  DivineServiceTypeDto,
  UpdateDivineServiceTypeDto,
} from '../../services/divine-service-type-management.service';

@Component({
  selector: 'app-divine-service-type-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    PageHeaderComponent,
    TableSurfaceComponent,
  ],
  templateUrl: './divine-service-type-list.component.html',
  styleUrl: './divine-service-type-list.component.scss',
})
export class DivineServiceTypeListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true },
    {
      key: 'displayName',
      header: 'Descripcion',
      hideable: true,
      visible: true,
      resizable: true,
      editable: true,
      editType: 'text',
    },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false },
  ];

  tableData: DivineServiceTypeDto[] = [];
  isLoading = false;
  isDragDropEnabled = true;
  savingCell?: { row: DivineServiceTypeDto; column: string };
  searchTerm = '';
  allTypes: DivineServiceTypeDto[] = [];

  private destroy$ = new Subject<void>();
  private isSaving = false;

  constructor(
    private service: DivineServiceTypeManagementService,
    private dialog: MatDialog,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createNew(): void {
    this.router.navigate(['/configuration/divine-service-types/new']);
  }

  editType(type: DivineServiceTypeDto): void {
    this.router.navigate(['/configuration/divine-service-types', type.value, 'edit']);
  }

  deleteType(type: DivineServiceTypeDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar Tipo de Servicio',
        message: `Estas seguro de que deseas eliminar el tipo "${type.displayName}"?`,
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.confirmDelete(type.value!);
        }
      });
  }

  onRowsReordered(event: any): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    const reorderedItems = event.data;
    const orderedIds = reorderedItems.map((item: DivineServiceTypeDto) => item.value);

    this.service.updateSortOrder(orderedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allTypes = [...reorderedItems];
          this.notificationService.success('Orden actualizado exitosamente');
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating sort order:', error);
          this.notificationService.error('Error al actualizar el orden. Recargando...');
          this.isSaving = false;
          this.loadTypes();
          this.cdr.markForCheck();
        },
      });
  }

  onToolbarSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  onCellEditSave(event: CellEditSaveEvent<DivineServiceTypeDto>): void {
    if (event.column !== 'displayName') {
      return;
    }

    const newDisplayName = String(event.newValue ?? '').trim();
    const oldDisplayName = String(event.oldValue ?? '');

    if (!newDisplayName) {
      this.notificationService.warning('La descripcion no puede quedar vacia');
      return;
    }

    if (newDisplayName === oldDisplayName) {
      return;
    }

    const type = event.row;
    this.savingCell = { row: type, column: event.column };
    this.cdr.markForCheck();

    const updateDto = new UpdateDivineServiceTypeDto({
      name: type.name,
      displayName: newDisplayName,
      offeringRequired: type.offeringRequired,
    });

    this.service.update(type.value!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          type.displayName = newDisplayName;
          this.tableData = [...this.tableData];
          this.notificationService.success('Tipo de servicio actualizado exitosamente');
          this.savingCell = undefined;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating divine service type:', error);
          type.displayName = oldDisplayName;
          this.notificationService.error((error.error as any)?.message || 'Error al actualizar el tipo de servicio');
          this.savingCell = undefined;
          this.loadTypes();
          this.cdr.markForCheck();
        },
      });
  }

  private loadTypes(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: types => {
          this.allTypes = types;
          this.applyFilter();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading divine service types:', error);
          this.notificationService.error('Error al cargar los tipos de servicios');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de servicio eliminado exitosamente');
          this.loadTypes();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error deleting divine service type:', error);
          this.notificationService.error((error.error as any)?.message || 'Error al eliminar el tipo de servicio');
          this.cdr.markForCheck();
        },
      });
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const filteredData = term
      ? this.allTypes.filter(type =>
          (type.name ?? '').toLowerCase().includes(term) ||
          (type.displayName ?? '').toLowerCase().includes(term)
        )
      : this.allTypes;

    this.tableData = [...filteredData];
    this.cdr.markForCheck();
  }
}
